import { GameButton, Modal, PixelIcon, CurrencyDisplay, type ModalTab } from '@/components/ui';
import { useGame, useShop, useSound } from '@/contexts';
import { motion, LayoutGroup, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR, SHADOW } from '@/config/designTokens';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PlayerProfile {
  name: string;
  username: string;
  createdAt: number;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { gameState } = useGame();
  const { shopState, shopManager } = useShop();
  const { playButtonClick } = useSound();
  
  // Get streak info
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };

  // Profile state
  const [profile, setProfile] = useState<PlayerProfile>(() => {
    const saved = localStorage.getItem('playerProfile');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      name: 'Player',
      username: `player${Math.floor(Math.random() * 10000)}`,
      createdAt: Date.now(),
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(profile.name);
  const [editUsername, setEditUsername] = useState(profile.username);
  const [activeTab, setActiveTab] = useState<'profile' | 'stats' | 'achievements'>('profile');

  // Calculate aggregated stats
  const totalLevelsCompleted = gameState.completedLevels.length;
  const totalStars = Object.values(gameState.levelStats).reduce(
    (sum, stats) => sum + (stats.stars || 0),
    0
  );
  const totalScore = Object.values(gameState.levelStats).reduce(
    (sum, stats) => sum + (stats.bestScore || 0),
    0
  );
  const totalPlayTime = Object.values(gameState.levelStats).reduce(
    (sum, stats) => sum + (stats.bestTime || 0),
    0
  );
  const totalAttempts = Object.values(gameState.levelStats).reduce(
    (sum, stats) => sum + (stats.totalAttempts || 0),
    0
  );

  // Calculate achievements
  const achievements = [
    {
      name: 'First Steps',
      description: 'Complete your first level',
      earned: totalLevelsCompleted > 0,
      icon: 'target',
    },
    {
      name: 'Star Collector',
      description: 'Earn 10 stars',
      earned: totalStars >= 10,
      icon: 'star',
    },
    {
      name: 'Perfectionist',
      description: 'Get 3 stars on any level',
      earned: Object.values(gameState.levelStats).some(stats => stats.stars === 3),
      icon: 'effects',
    },
    {
      name: 'Rich Player',
      description: 'Collect 1000 coins',
      earned: shopState.totalSpent.coins + shopState.coins >= 1000,
      icon: 'coins',
    },
    {
      name: 'Speed Runner',
      description: 'Complete a level in under 30 seconds',
      earned: Object.values(gameState.levelStats).some(stats => stats.bestTime < 30),
      icon: 'lightning',
    },
    {
      name: 'Persistent',
      description: 'Attempt levels 100 times',
      earned: totalAttempts >= 100,
      icon: 'strength',
    },
  ];

  const earnedAchievements = achievements.filter(a => a.earned).length;

  // Save profile changes
  const handleSaveProfile = () => {
    const newProfile = {
      ...profile,
      name: editName.trim() || 'Player',
      username: editUsername.trim() || profile.username,
    };
    setProfile(newProfile);
    localStorage.setItem('playerProfile', JSON.stringify(newProfile));
    setIsEditing(false);
    playButtonClick();
  };

  // Format time display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const tabs: ModalTab[] = [
    { id: 'profile', label: 'Profile', icon: 'user' },
    { id: 'stats', label: 'Statistics', icon: 'chart' },
    {
      id: 'achievements',
      label: 'Achievements',
      icon: 'trophy',
      count: earnedAchievements,
    },
  ];

  const headerContent = (
    <motion.div className="menu-toolbar profile-toolbar" layout>
      <CurrencyDisplay
        coins={shopState.coins}
        gems={shopState.gems}
        lives={shopState.lives}
        streak={streakStatus.streak}
        canClaimStreak={streakStatus.canClaim}
        variant="header"
        showStreak={true}
        animated={true}
      />
    </motion.div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Player Profile"
      subtitle="Progress, inventory, and run history."
      variant="success"
      size="large"
      className="profile-modal menu-facelift-modal"
      headerContent={headerContent}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(tabId) => setActiveTab(tabId as 'profile' | 'stats' | 'achievements')}
      layoutId="nav-button-profile"
      sourceButtonId="profile"
    >
      <LayoutGroup>
        <AnimatePresence mode="wait">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              className="profile-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {isEditing ? (
                <motion.div
                  className="profile-edit-form"
                  style={{ padding: SPACING.xxl, maxWidth: '400px', margin: '0 auto' }}
                  layout
                >
                  <motion.div className="form-group" style={{ marginBottom: SPACING.xl }} layout>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: SPACING.sm,
                        fontSize: FONT_SIZE.body,
                        color: COLOR.textSecondary,
                        fontWeight: 'bold',
                      }}
                    >
                      <div style={{ marginRight: SPACING.sm }}>
                        <PixelIcon name="user" size={14} />
                      </div>
                      Name
                    </label>
                    <motion.input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="Your name"
                      maxLength={20}
                      style={{
                        width: '100%',
                        padding: SPACING.md,
                        fontSize: FONT_SIZE.buttonLarge,
                        background: 'var(--pixel-bg-secondary)',
                        border: '2px solid var(--pixel-border)',
                        borderRadius: RADIUS.sm,
                        color: COLOR.textPrimary,
                        fontFamily: 'inherit',
                      }}
                      whileFocus={{ borderColor: 'var(--pixel-primary)' }}
                      layout
                    />
                  </motion.div>

                  <motion.div className="form-group" style={{ marginBottom: '30px' }} layout>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: SPACING.sm,
                        fontSize: FONT_SIZE.body,
                        color: COLOR.textSecondary,
                        fontWeight: 'bold',
                      }}
                    >
                      <div style={{ marginRight: SPACING.sm }}>
                        <PixelIcon name="settings" size={14} />
                      </div>
                      Username
                    </label>
                    <motion.input
                      type="text"
                      value={editUsername}
                      onChange={e => setEditUsername(e.target.value)}
                      placeholder="Your username"
                      maxLength={15}
                      style={{
                        width: '100%',
                        padding: SPACING.md,
                        fontSize: FONT_SIZE.buttonLarge,
                        background: 'var(--pixel-bg-secondary)',
                        border: '2px solid var(--pixel-border)',
                        borderRadius: RADIUS.sm,
                        color: COLOR.textPrimary,
                        fontFamily: 'inherit',
                      }}
                      whileFocus={{ borderColor: 'var(--pixel-primary)' }}
                      layout
                    />
                  </motion.div>

                  <motion.div
                    className="form-actions"
                    style={{ display: 'flex', gap: SPACING.md, justifyContent: 'center' }}
                    layout
                  >
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <GameButton onClick={handleSaveProfile} variant="success" icon="success" text="Save Changes" />
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <GameButton
                        onClick={() => {
                          setIsEditing(false);
                          setEditName(profile.name);
                          setEditUsername(profile.username);
                        }}
                        variant="secondary"
                        icon="close"
                        text="Cancel"
                      />
                    </motion.div>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  className="profile-display"
                  style={{ textAlign: 'center', padding: SPACING.section, maxWidth: '400px', margin: '0 auto' }}
                  layout
                >
                  <motion.div
                    className="profile-avatar"
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: RADIUS.full,
                      background: 'var(--pixel-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '40px',
                      border: '4px solid var(--pixel-border)',
                      margin: `0 auto ${SPACING.xxl}`,
                      color: COLOR.textWhite,
                      fontWeight: 'bold',
                    }}
                    whileHover={{ scale: 1.05 }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    layout
                  >
                    {profile.name.charAt(0).toUpperCase()}
                  </motion.div>
                  
                  <motion.h3
                    style={{ fontSize: FONT_SIZE.profileName, margin: `0 0 ${SPACING.sm} 0` }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {profile.name}
                  </motion.h3>
                  
                  <motion.p
                    style={{
                      fontSize: FONT_SIZE.profileUsername,
                      color: COLOR.textSecondary,
                      margin: `0 0 ${SPACING.sm} 0`,
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    @{profile.username}
                  </motion.p>
                  
                  <motion.p
                    style={{
                      fontSize: FONT_SIZE.body,
                      color: COLOR.textSecondary,
                      margin: '0 0 30px 0',
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    Playing since {formatDate(profile.createdAt)}
                  </motion.p>
                  
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <GameButton
                      onClick={() => {
                        setIsEditing(true);
                        playButtonClick();
                      }}
                      variant="info"
                      icon="settings"
                      text="Edit Profile"
                      className="edit-btn !mx-auto"
                    />
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              className="stats-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="modal-grid modal-grid--3-col"
                style={{ gap: SPACING.lg }}
                layout
              >
                {[
                  { icon: 'trophy', color: '#f39c12', value: totalLevelsCompleted, label: 'Levels Completed' },
                  { icon: 'star', color: '#ffd600', value: totalStars, label: 'Total Stars' },
                  { icon: 'target', color: '#2196f3', value: totalScore.toLocaleString(), label: 'Total Score' },
                  { icon: 'clock', color: '#607d8b', value: formatTime(totalPlayTime), label: 'Play Time' },
                  { icon: 'fire', color: '#e67e22', value: streakStatus.streak, label: 'Day Streak' },
                  { icon: 'retry', color: '#9c27b0', value: totalAttempts, label: 'Total Attempts' },
                  { icon: 'chart', color: '#4caf50', value: gameState.gameStats.streak || 0, label: 'Best Streak' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    className="stat-card"
                    style={{
                      background: 'var(--pixel-bg-secondary)',
                      border: '2px solid var(--pixel-border)',
                      borderRadius: RADIUS.lg,
                      padding: SPACING.xl,
                      textAlign: 'center',
                    }}
                    initial={{ opacity: 0, y: 30, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    layout
                  >
                    <motion.div
                      className="stat-icon"
                      style={{ marginBottom: SPACING.md }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <PixelIcon name={stat.icon} size={32} color={stat.color} />
                    </motion.div>
                    <motion.div
                      className="stat-value"
                      style={{
                        fontSize: FONT_SIZE.modalTitle,
                        fontWeight: 'bold',
                        marginBottom: SPACING.sm,
                        color: stat.color,
                      }}
                    >
                      {stat.value}
                    </motion.div>
                    <div
                      className="stat-label"
                      style={{ fontSize: FONT_SIZE.body, color: COLOR.textSecondary }}
                    >
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
                
                <motion.div 
                  className="stat-card spending-summary" 
                  style={{
                    gridColumn: '1 / -1',
                    background: 'var(--pixel-bg-secondary)',
                    border: '2px solid var(--pixel-border)',
                    borderRadius: RADIUS.lg,
                    padding: SPACING.xl,
                    textAlign: 'center',
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  layout
                >
                  <h4 style={{ fontSize: FONT_SIZE.buttonLarge, marginBottom: SPACING.md, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING.sm }}>
                    <PixelIcon name="shop" size={16} color={COLOR.statGreen} />
                    Total Spending
                  </h4>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING.lg, fontSize: FONT_SIZE.body, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                      <PixelIcon name="coin" size={16} color={COLOR.coin} />
                      <span>{shopState.totalSpent.coins.toLocaleString()} coins</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                      <PixelIcon name="gem" size={16} color={COLOR.gem} />
                      <span>{shopState.totalSpent.gems} gems</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <motion.div
              key="achievements"
              className="achievements-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="modal-grid modal-grid--auto-fit"
                style={{ gap: SPACING.lg }}
                layout
              >
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={achievement.name}
                    className={`achievement-card ${achievement.earned ? 'earned' : 'locked'}`}
                    style={{
                      background: achievement.earned
                        ? 'var(--pixel-success)'
                        : 'var(--pixel-bg-secondary)',
                      border: `3px solid ${achievement.earned ? 'var(--pixel-success)' : 'var(--pixel-border)'}`,
                      borderRadius: RADIUS.lg,
                      padding: SPACING.xl,
                      textAlign: 'center',
                      opacity: achievement.earned ? 1 : 0.6,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    initial={{ opacity: 0, scale: 0.8, y: 30 }}
                    animate={{ opacity: achievement.earned ? 1 : 0.6, scale: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ 
                      scale: achievement.earned ? 1.05 : 1.02, 
                      y: achievement.earned ? -4 : -2 
                    }}
                    layout
                  >
                    {achievement.earned && (
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: (index * 0.1) + 0.3, type: "spring", stiffness: 400 }}
                      >
                        <PixelIcon name="success" size={12} color="white" />
                      </motion.div>
                    )}
                    
                    <motion.div
                      style={{ marginBottom: SPACING.md }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {achievement.earned ? (
                        <PixelIcon name={achievement.icon} size={40} color={COLOR.textWhite} />
                      ) : (
                        <PixelIcon name="lock" size={40} color={COLOR.disabled} />
                      )}
                    </motion.div>

                    <h4 style={{ fontSize: FONT_SIZE.buttonLarge, margin: `0 0 ${SPACING.sm} 0`, color: achievement.earned ? COLOR.textWhite : 'var(--pixel-text-primary)' }}>
                      {achievement.name}
                    </h4>
                    <p style={{ fontSize: FONT_SIZE.buttonSmall, margin: 0, opacity: 0.9, color: achievement.earned ? COLOR.textWhite : COLOR.textSecondary }}>
                      {achievement.description}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Powerup Inventory (shown at bottom of all tabs) */}
        <motion.div
          style={{
            marginTop: SPACING.xxl,
            paddingTop: SPACING.xl,
            borderTop: '2px solid var(--pixel-border)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          layout
        >
          <h4 style={{ fontSize: FONT_SIZE.buttonLarge, marginBottom: SPACING.md, display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
            <PixelIcon name="lightning" size={16} color={COLOR.statGreen} />
            Powerup Inventory
          </h4>
          <motion.div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sm }} layout>
            <AnimatePresence>
              {Object.entries(shopState.powerups).map(([id, count]) => {
                if (count === 0) return null;
                return (
                  <motion.div
                    key={id}
                    style={{
                      background: 'var(--pixel-bg-secondary)',
                      padding: `${SPACING.sm} ${SPACING.md}`,
                      borderRadius: RADIUS.sm,
                      border: '2px solid var(--pixel-border)',
                      fontSize: FONT_SIZE.buttonSmall,
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.sm,
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    layout
                  >
                    <span style={{ color: 'var(--pixel-primary)', fontWeight: 'bold' }}>{count}x</span>
                    <PixelIcon name="lightning" size={12} color="var(--pixel-primary)" />
                    <span>{id}</span>
                  </motion.div>
                );
              })}
              {Object.values(shopState.powerups).every(count => count === 0) && (
                <motion.p 
                  style={{ fontSize: FONT_SIZE.body, color: COLOR.textSecondary, margin: 0 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  layout
                >
                  No powerups in inventory
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </LayoutGroup>
    </Modal>
  );
};

export default ProfileModal;
